import { Row, Col, Card, List, Tag, Empty, Spin, Select, DatePicker, Button, Avatar, Tooltip, Typography } from "antd";
import { UserOutlined, LeftOutlined } from "@ant-design/icons";
import React, { useState, useEffect, useCallback } from "react";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import moment from "moment";
import lang from "../helper/lang";

const { RangePicker } = DatePicker;
const { Text, Title, Paragraph } = Typography;

// Tabs match how the app already tags actors everywhere else (see
// ActivityLogHelper.resolveActorRole on the backend) — one tab per role,
// each populated from every User of that type so a name is clickable even
// before they have any recorded activity.
const ROLE_TABS = [
  { key: "client", label: lang("Client") },
  { key: "brand_manager", label: lang("Brand Manager") },
  { key: "brand_strategist", label: lang("Brand Strategist") },
  { key: "brand_head", label: lang("Brand Head") },
  { key: "assistant", label: lang("Assistant") },
  { key: "admin", label: lang("Admin") },
];

// Human-readable labels + colors for the raw action strings ActivityLog
// stores — kept in one place so a new action type added later just needs
// an entry here, not a UI restructure.
const ACTION_META = {
  "post.created": { label: lang("Created post"), color: "blue" },
  "post.edited": { label: lang("Edited post"), color: "geekblue" },
  "post.autosaved": { label: lang("Autosaved"), color: "default" },
  "post.deleted": { label: lang("Deleted post"), color: "red" },
  "post.edit_failed_not_found": { label: lang("Save FAILED — post not found"), color: "volcano" },
  "post.edited_into_deleted_post": { label: lang("Saved onto an already-deleted post"), color: "volcano" },
  "post.marked_published_externally": { label: lang("Marked published on LinkedIn (external)"), color: "cyan" },

  "blog.created": { label: lang("Created blog"), color: "blue" },
  "blog.edited": { label: lang("Edited blog"), color: "geekblue" },
  "blog.autosaved": { label: lang("Autosaved blog"), color: "default" },
  "blog.deleted": { label: lang("Deleted blog"), color: "red" },
  "blog.client_approved": { label: lang("Client approved blog"), color: "green" },
  "blog.client_rejected": { label: lang("Client rejected blog"), color: "red" },
  "blog.edit_failed_not_found": { label: lang("Save FAILED — blog not found"), color: "volcano" },
  "blog.edited_into_deleted_post": { label: lang("Saved onto an already-deleted blog"), color: "volcano" },

  "wa.draft_created": { label: lang("WA: Created draft"), color: "blue" },
  "wa.draft_updated": { label: lang("WA: Updated draft"), color: "geekblue" },
  "wa.draft_deleted": { label: lang("WA: Deleted draft"), color: "red" },
  "wa.content_generated": { label: lang("WA: Generated content"), color: "purple" },

  "ra.research_run": { label: lang("RA: Ran research"), color: "purple" },
  "ra.content_analyzed": { label: lang("RA: Analyzed content"), color: "purple" },
  "ra.guideline_checked": { label: lang("RA: Checked guidelines"), color: "purple" },
  "ra.claim_verified": { label: lang("RA: Verified claim"), color: "purple" },
  "ra.content_generated": { label: lang("RA: Generated content"), color: "purple" },
  "ra.history_saved": { label: lang("RA: Saved research history"), color: "blue" },
  "ra.history_deleted": { label: lang("RA: Deleted research history"), color: "red" },
  "ra.history_cleared_all": { label: lang("RA: Cleared all research history"), color: "red" },

  "presence.status_changed": { label: lang("Status changed"), color: "default" },

  "post.submitted_for_internal_review": { label: lang("Submitted post for internal review"), color: "gold" },
  "post.internal_approved": { label: lang("Internally approved post"), color: "green" },
  "post.changes_requested": { label: lang("Requested changes on post"), color: "orange" },
  "post.sent_to_client": { label: lang("Sent post to client"), color: "cyan" },
  "post.override_send_to_client": { label: lang("Override-sent post to client"), color: "volcano" },
  "post.client_approved": { label: lang("Client approved post"), color: "green" },
  "post.client_rejected": { label: lang("Client rejected post"), color: "red" },

  "blog.submitted_for_internal_review": { label: lang("Submitted blog for internal review"), color: "gold" },
  "blog.internal_approved": { label: lang("Internally approved blog"), color: "green" },
  "blog.changes_requested": { label: lang("Requested changes on blog"), color: "orange" },
  "blog.sent_to_client": { label: lang("Sent blog to client"), color: "cyan" },
  "blog.override_send_to_client": { label: lang("Override-sent blog to client"), color: "volcano" },

  "login": { label: lang("Logged in"), color: "default" },
  "logout": { label: lang("Logged out"), color: "default" },

  "contract.created": { label: lang("Created contract"), color: "blue" },
  "contract.renewed": { label: lang("Renewed contract"), color: "blue" },
  "contract.updated": { label: lang("Updated contract"), color: "geekblue" },
  "contract.deleted": { label: lang("Deleted contract"), color: "red" },
  "contract.payment_recorded": { label: lang("Recorded contract payment"), color: "green" },
  "contract.payment_deleted": { label: lang("Deleted contract payment"), color: "red" },

  "note.created": { label: lang("Created note"), color: "blue" },
  "note.updated": { label: lang("Updated note"), color: "geekblue" },
  "note.deleted": { label: lang("Deleted note"), color: "red" },

  "theme.created": { label: lang("Created content theme"), color: "blue" },
  "theme.updated": { label: lang("Updated content theme"), color: "geekblue" },
  "theme.deleted": { label: lang("Deleted content theme"), color: "red" },

  "vault.created": { label: lang("Created vault item"), color: "blue" },
  "vault.folder_created": { label: lang("Created vault folder"), color: "blue" },
  "vault.bulk_created": { label: lang("Bulk-uploaded vault items"), color: "blue" },
  "vault.updated": { label: lang("Updated vault item"), color: "geekblue" },
  "vault.deleted": { label: lang("Deleted vault item"), color: "red" },

  "marking.created": { label: lang("Created calendar marking"), color: "blue" },
  "marking.updated": { label: lang("Updated calendar marking"), color: "geekblue" },
  "marking.deleted": { label: lang("Deleted calendar marking"), color: "red" },
};

const actionMeta = (action) => ACTION_META[action] || { label: action, color: "purple" };

function ActivityLog() {
  const { request } = useRequest();

  const [activeTab, setActiveTab] = useState("client");
  const [actors, setActors] = useState([]);
  const [actorsLoading, setActorsLoading] = useState(false);
  const [selectedActor, setSelectedActor] = useState(null);

  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // Filters applied on top of whichever name is selected.
  const [actionFilter, setActionFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const fetchActors = useCallback(
    (role) => {
      setActorsLoading(true);
      setSelectedActor(null);
      setEntries([]);
      request({
        url: `${apiPath.activityLogActors}?role=${role}`,
        method: "GET",
        onSuccess: (data) => {
          setActors(data?.data?.actors || []);
          setActorsLoading(false);
        },
        onError: () => setActorsLoading(false),
      });
    },
    [request]
  );

  useEffect(() => {
    fetchActors(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchEntries = useCallback(
    (actor, pageNum = 1) => {
      if (!actor) return;
      setEntriesLoading(true);
      const params = new URLSearchParams({ actorId: actor._id, page: pageNum, limit: 50 });
      if (actionFilter) params.set("action", actionFilter);
      if (dateRange?.[0]) params.set("from", dateRange[0].startOf("day").toISOString());
      if (dateRange?.[1]) params.set("to", dateRange[1].endOf("day").toISOString());
      request({
        url: `${apiPath.activityLogList}?${params.toString()}`,
        method: "GET",
        onSuccess: (data) => {
          setEntries(data?.data?.entries || []);
          setTotal(data?.data?.total || 0);
          setEntriesLoading(false);
        },
        onError: () => setEntriesLoading(false),
      });
    },
    [request, actionFilter, dateRange]
  );

  useEffect(() => {
    if (selectedActor) {
      setPage(1);
      fetchEntries(selectedActor, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActor, actionFilter, dateRange]);

  const displayedEntries = flaggedOnly
    ? entries.filter((e) => e.action === "post.edit_failed_not_found" || e.action === "post.edited_into_deleted_post")
    : entries;

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title={lang("Activity Log")}
            style={{ marginBottom: 0 }}
          >
            <Text type="secondary">
              {lang(
                "Every meaningful action and content save — including background autosaves — across Clients, Brand Managers, Brand Strategists, Brand Heads, and Assistants. Click a name to see their full timeline."
              )}
            </Text>
          </Card>
        </Col>

        <Col span={24}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {ROLE_TABS.map((tab) => (
              <Button
                key={tab.key}
                type={activeTab === tab.key ? "primary" : "default"}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </Col>

        <Col xs={24} md={selectedActor ? 8 : 24}>
          <Card
            size="small"
            title={ROLE_TABS.find((t) => t.key === activeTab)?.label}
            bodyStyle={{ maxHeight: 620, overflowY: "auto", padding: 0 }}
          >
            {actorsLoading ? (
              <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>
            ) : actors.length === 0 ? (
              <Empty description={lang("No one in this role yet")} style={{ padding: 24 }} />
            ) : (
              <List
                dataSource={actors}
                renderItem={(actor) => (
                  <List.Item
                    onClick={() => setSelectedActor(actor)}
                    style={{
                      cursor: "pointer",
                      padding: "10px 16px",
                      background: selectedActor?._id === actor._id ? "#f0f5ff" : undefined,
                    }}
                  >
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} />}
                      title={actor.name || lang("Unnamed")}
                      description={actor.email}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {selectedActor && (
          <Col xs={24} md={16}>
            <Card
              size="small"
              title={
                <span>
                  <Button
                    type="text"
                    icon={<LeftOutlined />}
                    onClick={() => setSelectedActor(null)}
                    style={{ marginRight: 4 }}
                  />
                  {selectedActor.name || lang("Unnamed")}
                  <Text type="secondary" style={{ marginLeft: 8, fontWeight: 400, fontSize: 12 }}>
                    {selectedActor.email}
                  </Text>
                </span>
              }
              extra={<Text type="secondary">{total} {lang("entries")}</Text>}
            >
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <Select
                  allowClear
                  placeholder={lang("Filter by action")}
                  style={{ width: 220 }}
                  value={actionFilter}
                  onChange={setActionFilter}
                  options={Object.entries(ACTION_META).map(([value, meta]) => ({ value, label: meta.label }))}
                />
                <RangePicker value={dateRange} onChange={setDateRange} />
                <Button
                  type={flaggedOnly ? "primary" : "default"}
                  danger={flaggedOnly}
                  onClick={() => setFlaggedOnly((v) => !v)}
                >
                  {lang("Show only failed/suspicious saves")}
                </Button>
              </div>

              {entriesLoading ? (
                <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>
              ) : displayedEntries.length === 0 ? (
                <Empty description={lang("No activity found")} style={{ padding: 24 }} />
              ) : (
                <List
                  dataSource={displayedEntries}
                  pagination={
                    total > 50
                      ? {
                          current: page,
                          pageSize: 50,
                          total,
                          onChange: (p) => {
                            setPage(p);
                            fetchEntries(selectedActor, p);
                          },
                        }
                      : false
                  }
                  renderItem={(entry) => {
                    const meta = actionMeta(entry.action);
                    return (
                      <List.Item style={{ padding: "10px 4px" }}>
                        <div style={{ width: "100%" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                              <Tag color={meta.color}>{meta.label}</Tag>
                              {/* clientId was always recorded on post/blog
                                  entries, but nothing showed WHICH client a
                                  BM/BS/Admin's action was actually for —
                                  clientName is resolved server-side
                                  (ActivityLogController.listActivity) from
                                  that id. Skipped when the actor IS the
                                  client themselves — "Client Suhani · for
                                  Suhani" is redundant. */}
                              {entry.clientName && entry.actorRole !== "client" && (
                                <Tag color="blue">{lang("for")} {entry.clientName}</Tag>
                              )}
                              {entry.isAutosave && <Tag>{lang("autosave")}</Tag>}
                              {(entry.metadata?.captionChangedOnApprove || entry.metadata?.captionChangedOnRequestChanges) && (
                                <Tag color="purple">{lang("caption edited")}</Tag>
                              )}
                              {entry.targetLabel && (
                                <Text strong style={{ marginLeft: 4 }}>
                                  "{entry.targetLabel}"
                                </Text>
                              )}
                            </div>
                            <Tooltip title={moment(entry.created_at).format("DD MMM YYYY, HH:mm:ss")}>
                              <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                {moment(entry.created_at).fromNow()}
                              </Text>
                            </Tooltip>
                          </div>
                          {entry.contentPreview && (
                            <div
                              style={{
                                marginTop: 6,
                                padding: "8px 10px",
                                background: "#fafafa",
                                border: "1px solid #eee",
                                borderRadius: 6,
                                fontSize: 13,
                                color: "#555",
                              }}
                            >
                              {/* contentPreview now holds the FULL content of this
                                  action (not just a snippet) — collapsed to 3 lines
                                  by default with a "more" toggle so the timeline
                                  stays scannable while the complete text is always
                                  one click away. */}
                              <Paragraph
                                style={{ margin: 0, whiteSpace: "pre-wrap" }}
                                ellipsis={{ rows: 3, expandable: true, symbol: lang("Show full content") }}
                              >
                                {entry.contentPreview}
                              </Paragraph>
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {entry.contentLength} {lang("characters")}
                              </Text>
                            </div>
                          )}
                          {entry.metadata?.reason && (
                            <Text type="danger" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                              {entry.metadata.reason}
                            </Text>
                          )}
                          {entry.metadata?.comment && (
                            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4, fontStyle: "italic" }}>
                              "{entry.metadata.comment}"
                            </Text>
                          )}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              )}
            </Card>
          </Col>
        )}
      </Row>
    </>
  );
}

export default ActivityLog;
